import {
  DeleteIcon,
  LinkedEdgelessIcon,
  LinkedPageIcon,
} from '@blocksuite/icons/rc';
import type { DocCollection } from '@blocksuite/store';
import { useDocMetaHelper } from '@pique/core/hooks/use-block-suite-page-meta';
import {
  PeekViewService,
  useInsidePeekView,
} from '@pique/core/modules/peek-view';
import { WorkbenchLink } from '@pique/core/modules/workbench';
import { useI18n } from '@pique/i18n';
import {
  type DocMode,
  DocsService,
  LiveData,
  useLiveData,
  useService,
} from '@toeverything/infra';
import { type PropsWithChildren, useCallback, useRef } from 'react';

import * as styles from './styles.css';

export interface PageReferenceRendererOptions {
  docMode: DocMode | null;
  pageId: string;
  docCollection: DocCollection;
  pageMetaHelper: ReturnType<typeof useDocMetaHelper>;
  t: ReturnType<typeof useI18n>;
}
// use a function to be rendered in the lit renderer
export function pageReferenceRenderer({
  docMode,
  pageId,
  pageMetaHelper,
  t,
}: PageReferenceRendererOptions) {
  const referencedPage = pageMetaHelper.getDocMeta(pageId);
  const title =
    referencedPage?.title ?? t['com.pique.editor.reference-not-found']();

  const icon = !referencedPage ? (
    <DeleteIcon className={styles.pageReferenceIcon} />
  ) : docMode === 'page' || docMode === null ? (
    <LinkedPageIcon className={styles.pageReferenceIcon} />
  ) : (
    <LinkedEdgelessIcon className={styles.pageReferenceIcon} />
  );


  return (
    <>
      {icon}
      <span className="affine-reference-title">
        {title ? title : t['Untitled']()}
      </span>
    </>
  );
}

export function PiquePageReference({
  pageId,
  docCollection,
  wrapper: Wrapper,
}: {
  docCollection: DocCollection;
  pageId: string;
  wrapper?: React.ComponentType<PropsWithChildren>;
}) {
  const pageMetaHelper = useDocMetaHelper(docCollection);
  const t = useI18n();

  const docsService = useService(DocsService);
  const docPrimaryMode = useLiveData(
    LiveData.computed((get: <T>(data: LiveData<T>) => T) => {
      const primaryMode$ = get(docsService.list.doc$(pageId))?.primaryMode$;
      if (!primaryMode$) {
        return null;
      }
      return get(primaryMode$);
    })
  );
  const el = pageReferenceRenderer({
    docMode: docPrimaryMode,
    pageId,
    pageMetaHelper,
    docCollection,
    t,
  });

  const ref = useRef<HTMLAnchorElement>(null);

  const peekView = useService(PeekViewService).peekView;
  const isInPeekView = useInsidePeekView();

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.shiftKey && ref.current) {
        e.preventDefault();
        e.stopPropagation();
        peekView.open(ref.current).catch(console.error);
      }
      if (isInPeekView) {
        peekView.close();
      }
      return;
    },
    [isInPeekView, peekView]
  );

  return (
    <WorkbenchLink
      ref={ref}
      to={`/${pageId}`}
      onClick={onClick}
      className={styles.pageReferenceLink}
    >
      {Wrapper ? <Wrapper>{el}</Wrapper> : el}
    </WorkbenchLink>
  );
}
